import { observable, toJS, makeObservable, action } from "mobx";
import _ from "lodash";

import { extractErrorMessage } from "../../../utils/helpers";
import { CrudCommon } from "../_common/CrudCommon";
import { AppStore } from "~/stores/AppStore";

import { translateKey as t } from "../../../locales/translation";

const DEFAULT_TIMEZONE = "UTC";

const newCronjobTemplate = (clientId: string, clientRef: string) => ({
  name: "",
  active: true,
  clientId,
  clientRef,
  type: null,
  payload: {},
  payloadJson: "{}",
  schedule: {
    frequency: "daily",
    daysOfWeek: [],
    time: "09:00",
  },
  timezone: DEFAULT_TIMEZONE,
});

export class CronjobController {
  public store: AppStore;
  public clientId: string;
  public clientRef: string;
  public clientName: string = "";

  public cronjob: any = null;
  public cronjobs: Array<any> = [];
  public cronjobTypes: Array<any> = [];

  public formRef: any;
  public crudCommon: CrudCommon;
  public formMode: "create" | "edit" = "create";
  public entitySnapshot = "";

  public mainDataHasLoaded = false;
  public additionalColumnsAreVisible = false;
  public isReadOnly = false;
  public isFormModalVisible = false;
  public saveButtonLoading = false;
  public tableInstance: any = null;
  public deleteModal: any = { visible: false, description: "", onConfirmDelete: () => this.deleteCronjobs() };
  public tableIsReadOnly = true;

  public modifiedRows: any = [];
  public multiSelectKeyPressed = false;
  public shiftKeyPressed = false;
  public clickedRow: any = {};

  public modalOptionsLocalStorageKey = "cronjobsFormModalOptions";
  public formFieldsSize: "small" | "middle" | "large" = "middle";
  public formModalSize: "small" | "default" | "large" = "default";
  public showNonMandatoryFields = true;
  public showFieldsIcon = false;
  public prerequisitesLoaded = true;

  constructor(store: AppStore, clientId: string, clientRef: string) {
    this.store = store;
    this.clientId = clientId;
    this.clientRef = clientRef;
    this.crudCommon = new CrudCommon(this, "cronjobs");

    makeObservable(this, {
      cronjobs: observable,
      cronjob: observable,
      cronjobTypes: observable,
      clientName: observable,
      formRef: observable,
      crudCommon: observable,
      formMode: observable,
      entitySnapshot: observable,
      mainDataHasLoaded: observable,
      additionalColumnsAreVisible: observable,
      isReadOnly: observable,
      isFormModalVisible: observable,
      saveButtonLoading: observable,
      tableInstance: observable,
      deleteModal: observable,
      tableIsReadOnly: observable,
      modifiedRows: observable,
      multiSelectKeyPressed: observable,
      shiftKeyPressed: observable,
      clickedRow: observable,

      setCronjobs: action,
      setCronjob: action,
      setCronjobTypes: action,
      setClientName: action,
      setMainDataHasLoaded: action,
      setIsFormModalVisible: action,
      setSaveButtonLoading: action,
      setFormMode: action,
      setEntitySnapshot: action,
      setEntity: action,
      setTableInstance: action,
      setAdditionalColumnsAreVisible: action,
    });

    this.isReadOnly = this.store.isRouteReadOnly(window.location.pathname);
    this.setFormModalOptions();
  }

  public setEntity(entity: string, value: any) {
    this[entity] = value;
  }
  public setCronjobs(value: Array<any>) {
    this.cronjobs = value;
  }
  public setCronjobTypes(value: Array<any>) {
    this.cronjobTypes = value;
  }
  public setCronjob(value: any, key: string = null) {
    if (key === null) {
      this.cronjob = value;
    } else {
      this.cronjob[key] = value;
    }
  }
  public setClientName(value: string) {
    this.clientName = value;
  }
  public setSaveButtonLoading(value: boolean) {
    this.saveButtonLoading = value;
  }
  public setAdditionalColumnsAreVisible(value: boolean) {
    this.additionalColumnsAreVisible = value;
  }
  public setIsFormModalVisible(value: boolean) {
    this.isFormModalVisible = value;
  }
  public setTableInstance(value: any) {
    this.tableInstance = value;
  }
  public setMainDataHasLoaded(value: any) {
    this.mainDataHasLoaded = value;
  }
  public setFormRef(value: any) {
    this.formRef = value;
  }
  public setEntitySnapshot(value: any) {
    this.entitySnapshot = value;
  }
  public setFormMode(value: "create" | "edit") {
    this.formMode = value;
  }

  public async loadDependencies() {
    try {
      await this.loadClient();
      await this.loadCronjobTypes();
      await this.loadCronjobs();
      this.setMainDataHasLoaded(true);
    } catch (err) {
      this.store.displayNotification(t("lblFailedToLoadCronjobs", "Failed to Load Cronjobs"), "error", true, extractErrorMessage(err));
    }
  }

  private async loadCronjobTypes() {
    try {
      const types = await this.store.getData("cronjob-types?activeOnly=true");
      this.setCronjobTypes(types || []);
    } catch (err) {
      this.store.displayNotification(t("lblFailedToLoadCronjobTypes", "Failed to Load Cronjob Types"), "error", true, extractErrorMessage(err));
    }
  }

  private async loadClient() {
    try {
      const client = await this.store.getData("clients/" + this.clientId, "pro_client");

      console.log("Loaded client for cronjobs:", client);
      if (client) {
        this.setClientName(client.name || client.clientRef || this.clientRef);
        if (client.clientRef && !this.clientRef) {
          this.clientRef = client.clientRef;
        }
      }
    } catch (err) {
      // Non-fatal — header just falls back to clientRef.
    }
  }

  private async loadCronjobs() {
    const cronjobs = await this.store.getData("cronjobs?clientId=" + encodeURIComponent(this.clientId));
    this.setCronjobs(cronjobs || []);
    this.setCronjob(null);
  }

  public openEditForm = async (cronjob: any) => {
    this.setFormMode("edit");
    // List response omits payload (it lives in blob storage). Fetch the full
    // record so the form renders with the saved payload.
    let full = cronjob;
    try {
      const fetched = await this.store.getData("cronjobs/" + cronjob._id);
      if (fetched) full = fetched;
    } catch (err) {
      this.store.displayNotification(t("lblFailedToLoadCronjob", "Failed to Load Cronjob"), "error", true, extractErrorMessage(err));
    }
    const draft = toJS(full);
    draft.payloadJson = JSON.stringify(full.payload || {}, null, 2);
    this.cronjob = draft;
    this.setEntitySnapshot(JSON.stringify(this.cronjob));
    this.setIsFormModalVisible(true);
  };

  public setNewCronjob() {
    this.setFormMode("create");
    this.setCronjob(newCronjobTemplate(this.clientId, this.clientRef));
    this.setEntitySnapshot(JSON.stringify(this.cronjob));
  }

  private buildPayload() {
    const draft = toJS(this.cronjob);

    let parsedPayload: any;
    try {
      parsedPayload = JSON.parse(draft.payloadJson || "{}");
    } catch (err) {
      throw new Error(t("lblPayloadIsNotValidJSON", "Payload is not valid JSON"));
    }
    if (parsedPayload === null || typeof parsedPayload !== "object") {
      throw new Error(t("lblPayloadMustBeAJSONObjectOrArray", "Payload must be a JSON object or array"));
    }

    const authToken = this.store.user && this.store.user.ofAccessTokenClient;
    if (!authToken) {
      throw new Error(t("lblMissingClientToken", "Missing client token. Please re-authenticate against the client."));
    }

    const payload: any = {
      name: (draft.name || "").trim(),
      active: draft.active !== false,
      type: draft.type,
      payload: parsedPayload,
      schedule: {
        frequency: draft.schedule.frequency,
        daysOfWeek: draft.schedule.frequency === "weekly" ? draft.schedule.daysOfWeek || [] : [],
        time: draft.schedule.time,
      },
      timezone: (draft.timezone || DEFAULT_TIMEZONE).trim(),
      authToken,
    };

    if (this.formMode === "create") {
      payload.clientId = this.clientId;
      payload.clientRef = this.clientRef;
    }

    return payload;
  }

  public saveFormCronjob = async () => {
    this.setSaveButtonLoading(true);
    try {
      const payload = this.buildPayload();

      if (this.formMode === "create") {
        const response = await this.store.postData("cronjobs", payload);
        this.cronjobs.push(observable(response));
        this.reorderTable();
        this.setIsFormModalVisible(false);
        this.tableInstance && this.tableInstance.handleRowSelection && this.tableInstance.handleRowSelection(response);
        this.store.displayNotification(t("lblCronjobCreatedSuccessfully", "Cronjob Created Successfully"), "success", true, t("lblName", "Name") + ": " + payload.name);
      } else {
        const updated = await this.store.postData("cronjobs/" + this.cronjob._id, payload);
        const idx = this.cronjobs.findIndex((c) => c._id === this.cronjob._id);
        if (idx > -1) this.cronjobs[idx] = updated;
        this.reorderTable();
        this.setIsFormModalVisible(false);
        this.tableInstance && this.tableInstance.handleRowSelection && this.tableInstance.handleRowSelection(updated, false, true);
        this.store.displayNotification(t("lblCronjobUpdatedSuccessfully", "Cronjob Updated Successfully"), "success", true, t("lblName", "Name") + ": " + payload.name);
      }
    } catch (err) {
      this.store.displayNotification(
        this.formMode === "create" ? t("lblFailedToCreateCronjob", "Failed to Create Cronjob") : t("lblFailedToUpdateCronjob", "Failed to Update Cronjob"),
        "error",
        true,
        extractErrorMessage(err)
      );
    } finally {
      this.setSaveButtonLoading(false);
    }
  };

  public resetLastRun = async (cronjobId: string) => {
    try {
      const updated = await this.store.postData("cronjobs/" + cronjobId + "/reset-last-run", {});
      const idx = this.cronjobs.findIndex((c) => c._id === cronjobId);
      if (idx > -1) this.cronjobs[idx] = updated;
      if (this.cronjob && this.cronjob._id === cronjobId) {
        this.setCronjob({ ...this.cronjob, lastRunAt: null, lastRunStatus: null, lastRunError: null });
        this.setEntitySnapshot(JSON.stringify(this.cronjob));
      }
      this.store.displayNotification(t("lblLastRunReset", "Last run cleared. Worker will pick up this cronjob on the next tick."), "success", true);
    } catch (err) {
      this.store.displayNotification(t("lblFailedToResetLastRun", "Failed to reset last run"), "error", true, extractErrorMessage(err));
    }
  };

  public deleteCronjobs = async () => {
    const selectedRows: any = Object.values(this.tableInstance.multiSelectedRows.rows);
    const selectedRowsIds = selectedRows.map((id) => ({ _id: id }));
    const selectedRefs: string[] = selectedRowsIds.map((r) => this.cronjobs.find((c) => c._id === r._id)?.name).filter(Boolean);

    try {
      await this.store.postData("cronjobs/bulkRemove", { items: selectedRowsIds });
      this.cronjobs = this.cronjobs.filter((c) => !selectedRows.some((id) => id === c._id));
      this.modifiedRows = this.modifiedRows.filter((mr) => !selectedRowsIds.some((sr) => sr._id === mr.filter._id));
      this.tableInstance.resetMultiselectedRows();

      this.store.displayNotification(
        selectedRowsIds.length === 1 ? t("lblCronjobDeletedSuccessfully", "Cronjob Deleted Successfully") : t("lblCronjobsDeletedSuccessfully", "Cronjobs Deleted Successfully"),
        "success",
        true,
        selectedRefs.join(", ")
      );
    } catch (err) {
      this.store.displayNotification(t("lblFailedToDeleteCronjobs", "Failed to Delete Cronjobs"), "error", true, extractErrorMessage(err));
    }
  };

  private reorderTable = () => {
    this.cronjobs = _.sortBy(this.cronjobs, ["name"]);
  };

  public handleContextMenuEdit = (row: any) => {
    this.tableInstance.handleRowClick(row);
    this.openEditForm(row);
  };

  public handleContextMenuDelete = (row: any) => {
    if (this.tableInstance.multiSelectedRows.totSelected === 0) {
      this.multiSelectKeyPressed = true;
      this.tableInstance.handleRowClick(row);
      this.tableInstance.handleCellEdit();
      this.multiSelectKeyPressed = false;

      this.deleteModal.onConfirmDelete = () => this.deleteCronjobs();
      this.deleteModal.description = t("lblAreYouSureYouWantToDeleteCronjob", 'Are you sure you want to delete cronjob "{1}"?', [row.name]);
      this.deleteModal.visible = true;
    } else {
      this.deleteModal.onConfirmDelete = () => this.deleteCronjobs();
      this.deleteModal.description = t("lblAreYouSureYouWantToDeleteCountItems", "Are you sure you want to delete {1} items?", [this.tableInstance.multiSelectedRows.totSelected]);
      this.deleteModal.visible = true;
    }
  };

  private setFormModalOptions = () => {
    if (localStorage.getItem(this.modalOptionsLocalStorageKey)) {
      const obj: any = JSON.parse(localStorage.getItem(this.modalOptionsLocalStorageKey));
      this.formModalSize = obj.formModalSize.currentlySet;
      this.formFieldsSize = obj.formFieldsSize.currentlySet;
      this.showNonMandatoryFields = obj.showNonMandatoryFields.currentlySet;
      this.showFieldsIcon = obj.showFieldsIcon.currentlySet;
    } else {
      const opts = {
        formModalSize: { default: "default", currentlySet: "default" },
        formFieldsSize: { default: "default", currentlySet: "default" },
        showNonMandatoryFields: { default: true, currentlySet: true },
        showFieldsIcon: { default: false, currentlySet: false },
      };
      localStorage.setItem(this.modalOptionsLocalStorageKey, JSON.stringify(opts));
    }
  };
}
